import { BarElement, CategoryScale, Chart as ChartJS, Legend, LinearScale, Title, Tooltip } from 'chart.js';
import React from 'react';
import { Bar } from 'react-chartjs-2';
import { Link } from 'react-router-dom';
import { DashBoardDonutCard, IDashboardDonutChartDataSet } from '../../components/UI-Elements/DashBoardDonutCard';
import { StackedBarChartElement } from '../../sp-types/dashboardTypes';
import { FormatToShortPriceTemplate, formatToShortString } from '../../utils/FormatValue';
import {
  MonthYear,
  changeMonthYearBy,
  convertDateToMonthYear,
  getMonthName,
  months,
  sortMonthYear,
} from '../../utils/MonthYear/monthYear';
import { GENERAL_ID } from '../../components/Stores/exposureDataStore';
import { getUnitObj } from '../../utils/MassUnits/massUnitsUtils';
import { MULTIPLE_MATS, NO_DATA } from './dashboardUtils';
import { useMaterialsStore } from '../../components/Stores/MaterialsStore';
import { MonthYearValue } from '../../utils/MonthYear/MonthYearValue';
import { calcMaxVal } from './dashboard-components/DashValuesUtils';
import { Tooltip as MuiTooltip } from '@mui/material';
import { Visibility } from '@mui/icons-material';
import { useFormulasPrices } from '../../hooks/useFormulasPrices';
import { useAccountStore } from '../../components/Stores/accountStore';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

interface IStackedBarDoughnutChartCard {
  data: StackedBarChartElement[];
  accumulated: StackedBarChartElement[];
  cardTitle: string;
  cardDescription: string;
  cardUrl: string;
  formulaId: string;
  currentDate?: Date;
  addNewUrl?: string;
  terminals: number[];
  isTerminals: boolean;
  setIsTerminals: (_isTerminals: boolean) => void;
  isQuantity?: boolean;
  setIsQuantity: (_isQuan: boolean) => void;
  showDetails?: () => void;
  setMaxVal: (_max: number) => void;
  chartMax: number;
  showProportion: boolean;
  compact?: boolean;
  allFormulasTotal?: number; // Total value across all formulas (for proportion display in specific mode)
  annotation?: string; // Optional extra info line shown below the title
}

export const selectBlue = '#6571ff';

function sumValueArrayValues(elements: StackedBarChartElement[]): number {
  let totalSum = 0;

  // Iterate over each StackedBarChartElement
  elements.forEach((element) => {
    // Iterate over each value in the valueArray of the current element
    element.valueArray.forEach((valueObject) => {
      // Add the value to the total sum
      totalSum += valueObject.value;
    });
  });

  return totalSum;
}

export const StackedBarDoughnutChartCard: React.FC<IStackedBarDoughnutChartCard> = ({
  data,
  accumulated,
  cardTitle,
  cardDescription,
  cardUrl,
  formulaId,
  currentDate = new Date(),
  addNewUrl,
  terminals,
  isTerminals,
  setIsTerminals: _setIsTerminals,
  isQuantity = false,
  setIsQuantity: _setIsQuantity,
  showDetails,
  setMaxVal,
  chartMax,
  showProportion,
  compact = false,
  allFormulasTotal,
  annotation,
}) => {
  const { materialsMap } = useMaterialsStore();
  const { account } = useAccountStore();
  const { data: pricesData } = useFormulasPrices();
  const pricesMap = pricesData?.pricesMap || {};
  const baseCurrency = account?.baseCurrency || 'USD';
  
  const [ratioLine, setRatioLine] = React.useState<string>('');

  React.useEffect(() => {
    setRatioLine(
      cardTitle === 'Financial positions' && formulaId !== GENERAL_ID && materialsMap[formulaId]?.hasRatio
        ? 'Ratio - 0mt'
        : '',
    );
  }, [formulaId, materialsMap]);
  // Function to convert a single StackedBarChartElement to IDashboardDonutChartDataSet
  function convertToDonutChartDataSet(element: StackedBarChartElement, currentDate: Date): IDashboardDonutChartDataSet {
    // Use sumValue if available (total across all months), otherwise use current month value
    const value = element.sumValue !== undefined 
      ? Number(element.sumValue) // Ensure it's a number, not a string
      : Number(element.valueArray.find((item) => item.date.month === currentDate.getMonth() + 1 && item.date.year === currentDate.getFullYear())?.value || 0);

    return {
      fieldName: element.fieldName,
      color: element.color,
      value: value,
      unit: element.unit,
      price: element.price,
      ratio: 0,
    };
  }

  // Function to convert an array of StackedBarChartElement to an array of IDashboardDonutChartDataSet
  function convertArrayToDonutChartDataSetArray(
    elements: StackedBarChartElement[],
    currentDate: Date,
  ): IDashboardDonutChartDataSet[] {
    return elements.map((element) => convertToDonutChartDataSet(element, currentDate));
  }

  const donutChartData = convertArrayToDonutChartDataSetArray(accumulated, currentDate).map((item) => ({
    ...item,
    origVal: item.value,
    value: item.value, // accumulated data already contains correct values (quantity if isQuantity=true, monetary if isQuantity=false)
  }));

  const materialExposures = donutChartData.filter((a) => !!a.fieldName);

  // Compute both monetary and quantity totals from accumulated data for dual-row donut center
  const isSpecificMode = formulaId !== GENERAL_ID;
  const totalMonetary = React.useMemo(
    () => accumulated.filter(e => e.fieldName && e.fieldName !== 'Other formulas').reduce((s, e) => s + (e.sumMonetary || 0), 0),
    [accumulated],
  );
  const totalQty = React.useMemo(
    () => accumulated.filter(e => e.fieldName && e.fieldName !== 'Other formulas').reduce((s, e) => s + (e.sumQuantity || 0), 0),
    [accumulated],
  );
  const qtyUnitLabel = isSpecificMode && materialExposures?.[0]?.unit
    ? (getUnitObj(materialExposures[0].unit)?.shortForm || 'MT')
    : 'MT';

  // In specific mode, add a "remainder" slice to show proportion relative to all formulas
  const finalDonutChartData = [...donutChartData];
  if (isSpecificMode && allFormulasTotal && materialExposures.length > 0) {
    const specificFormulaTotal = materialExposures.reduce((sum, item) => sum + item.value, 0);
    const remainder = allFormulasTotal - specificFormulaTotal;
    
    if (remainder > 0) {
      // Add a gray "Other formulas" slice to show proportion
      finalDonutChartData.push({
        fieldName: 'Other formulas',
        color: '#E5E7EB', // Light gray
        value: remainder,
        origVal: remainder,
        unit: materialExposures[0]?.unit,
        price: 0,
        ratio: 0,
      });
    }
  }

  // Calculate totals - accumulated data already has correct values based on isQuantity flag
  const totalQuantityMT = isQuantity 
    ? materialExposures.reduce((sum, item) => sum + item.origVal, 0)
    : 0; // Not applicable in monetary mode

  // Generate human-readable calculation breakdown
  const calcDebugText = React.useMemo(() => {
    const lines: string[] = [];
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    const isGeneralMode = formulaId === GENERAL_ID;
    
    lines.push(`${cardTitle} - Calculation Breakdown`);
    lines.push('='.repeat(50));
    lines.push('');
    
    if (isGeneralMode) {
      // General mode: show breakdown by formulas (monetary only)
      lines.push('Mode: All Formulas');
      lines.push(`Base Currency: ${baseCurrency}`);
      lines.push('');
      lines.push('='.repeat(50));
      lines.push('');
      lines.push('MONETARY BREAKDOWN BY FORMULA');
      lines.push('='.repeat(50));
      lines.push('');
      
      let grandTotal = 0;
      const allFormulas = data.filter(d => d.formulaId !== 'placeholder');
      
      allFormulas.forEach((formula) => {
        const formulaName = materialsMap[formula.formulaId]?.name || formula.fieldName;
        const spotPrice = formula.price || pricesMap[formula.formulaId] || 0;
        const materialUnit = formula.unit ? (getUnitObj(formula.unit)?.shortForm || 'MT') : 'MT';
        const monetarySum = formula.sumMonetary || formula.sumValue || 0;
        
        lines.push(`${formulaName} (${formula.formulaId})`);
        lines.push(`  Spot price: ${spotPrice.toLocaleString()} ${baseCurrency}/${materialUnit}`);
        
        // Show on-hand breakdown if exists
        const formulaQuantityArray = formula.quantityArray || formula.valueArray;
        const onHandEntry = formulaQuantityArray?.find(({ date }) => date.month === 0 && date.year === 0);
        if (onHandEntry && onHandEntry.value !== 0) {
          const onHandQty = typeof onHandEntry.value === 'number' ? onHandEntry.value : parseFloat(String(onHandEntry.value)) || 0;
          const onHandValue = onHandQty * spotPrice;
          lines.push(`    On-hand: ${onHandQty.toLocaleString()} ${materialUnit} = ${onHandValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${baseCurrency}`);
        }
        
        lines.push(`  Total value: ${monetarySum.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${baseCurrency}`);
        lines.push('');
        
        grandTotal += monetarySum;
      });
      
      lines.push('='.repeat(50));
      lines.push(`GRAND TOTAL: ${grandTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${baseCurrency}`);
      
      return lines.join('\n');
    }
    
    // Specific formula mode
    const mainData = data.find(d => d.formulaId !== 'placeholder');
    if (!mainData) {
      lines.push('No data available');
      return lines.join('\n');
    }
    
    const formulaName = materialsMap[mainData.formulaId]?.name || mainData.fieldName;
    lines.push(`Formula: ${formulaName} (${mainData.formulaId})`);
    lines.push(`Base Currency: ${baseCurrency}`);
    lines.push('');
    lines.push('='.repeat(50));
    lines.push('');
    
    const spotPrice = mainData.price || pricesMap[mainData.formulaId] || 0;
    const materialUnit = mainData.unit ? (getUnitObj(mainData.unit)?.shortForm || 'MT') : 'MT';
    
    // QUANTITY SECTION
    lines.push('QUANTITY MODE');
    lines.push('='.repeat(50));
    lines.push('');
    
    const quantityArray = mainData.quantityArray || mainData.valueArray;
    
    // Separate on-hand from monthly data
    const onHandEntry = quantityArray.find(({ date }) => date.month === 0 && date.year === 0);
    const monthlyEntries = quantityArray.filter(({ date }) => !(date.month === 0 && date.year === 0));
    
    // Show on-hand first if it exists
    if (onHandEntry && onHandEntry.value !== 0) {
      const numValue = typeof onHandEntry.value === 'number' ? onHandEntry.value : parseFloat(String(onHandEntry.value)) || 0;
      const sign = numValue > 0 ? '+' : '';
      lines.push(`On-hand: ${sign}${numValue.toLocaleString()} ${materialUnit}`);
      lines.push('');
    }
    
    // Then show monthly breakdown
    monthlyEntries.forEach(({ date, value }) => {
      const numValue = typeof value === 'number' ? value : parseFloat(String(value)) || 0;
      if (numValue !== 0) {
        const sign = numValue > 0 ? '+' : '';
        lines.push(`${monthNames[date.month - 1]} ${date.year}: ${sign}${numValue.toLocaleString()} ${materialUnit}`);
      }
    });
    
    lines.push('');
    lines.push(`Summary: ${(mainData.sumQuantity || mainData.sumValue || 0).toLocaleString()} ${materialUnit}`);
    lines.push('');
    lines.push('');
    
    // MONETARY SECTION
    lines.push('MONETARY MODE');
    lines.push('='.repeat(50));
    lines.push('');
    
    // Get original price info if available
    const priceInfo = pricesMap[mainData.formulaId];
    if (priceInfo && priceInfo.originalCurrency && priceInfo.originalCurrency !== baseCurrency) {
      const fxRate = priceInfo.price / (priceInfo.originalPrice || priceInfo.price);
      lines.push(`Original spot price: ${(priceInfo.originalPrice || spotPrice).toLocaleString()} ${priceInfo.originalCurrency}/${materialUnit}`);
      lines.push(`FX rate: 1 ${baseCurrency} = ${(1/fxRate).toFixed(4)} ${priceInfo.originalCurrency}`);
      lines.push(`Converted price: ${spotPrice.toLocaleString()} ${baseCurrency}/${materialUnit}`);
    } else {
      lines.push(`Spot price: ${spotPrice.toLocaleString()} ${baseCurrency}/${materialUnit}`);
    }
    lines.push('');
    lines.push('Monthly breakdown:');
    lines.push('');
    
    let monetarySum = 0;
    
    // Show on-hand first if it exists
    if (onHandEntry && onHandEntry.value !== 0) {
      const numValue = typeof onHandEntry.value === 'number' ? onHandEntry.value : parseFloat(String(onHandEntry.value)) || 0;
      const monetaryValue = numValue * spotPrice;
      monetarySum += monetaryValue;
      const sign = numValue > 0 ? '+' : '';
      lines.push(`On-hand: ${sign}${numValue.toLocaleString()} ${materialUnit} × ${spotPrice.toLocaleString()} = ${sign}${monetaryValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${baseCurrency}`);
      lines.push('');
    }
    
    // Then show monthly breakdown
    monthlyEntries.forEach(({ date, value }) => {
      const numValue = typeof value === 'number' ? value : parseFloat(String(value)) || 0;
      if (numValue !== 0) {
        const monetaryValue = numValue * spotPrice;
        monetarySum += monetaryValue;
        const sign = numValue > 0 ? '+' : '';
        lines.push(`${monthNames[date.month - 1]} ${date.year}: ${sign}${numValue.toLocaleString()} ${materialUnit} × ${spotPrice.toLocaleString()} = ${sign}${monetaryValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${baseCurrency}`);
      }
    });
    
    lines.push('');
    lines.push(`Summary: ${monetarySum.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${baseCurrency}`);
    
    return lines.join('\n');
  }, [cardTitle, data, pricesMap, baseCurrency, materialsMap]);

  // const currentBarData =
  //   sumValueArrayValues(data) !== 0
  //     ? data
  //     : cardTitle == 'Financial positions'
  //     ? emptyMockFinancialPositionsData
  //     : emptyMockFutureOrdersData;
  return (
    <div className="flex  flex-col h-full bg-white p-3 overflow-auto ">
      <div className="flex flex-row justify-between items-center mb-3">
        <div className="flex flex-col gap-0.5">
          <div className="flex items-center gap-1.5">
            <h4 className="text-lg font-semibold">{cardTitle}</h4>
          </div>
          <p className="text-[10px] text-gray-500 h-3">
            {!isQuantity ? 'Values are based on latest spot prices (FX converted)' : '\u00A0'}
          </p>
          {annotation && (
            <p className="text-xs text-gray-400 mt-0.5">{annotation}</p>
          )}
        </div>
        <MuiTooltip title="Show details" arrow placement="top">
          <span>
            <button
              onClick={showDetails}
              disabled={!showDetails}
              className={`inline-flex items-center justify-center h-8 w-8 rounded-full transition-colors duration-150 ${
                showDetails
                  ? 'bg-gray-100 text-gray-700 hover:bg-gray-200 cursor-pointer'
                  : 'bg-gray-100 text-gray-400 cursor-not-allowed'
              }`}
            >
              <Visibility sx={{ fontSize: 16 }} />
            </button>
          </span>
        </MuiTooltip>
      </div>

      <div className="flex flex-col flex-1 justify-between">
        {!compact && (
          <div className="flex flex-col flex-1 items-center h-44">
            <DashBoardDonutCard
              dataSet={finalDonutChartData}
              // Alternate total as secondary row: value mode shows qty, qty mode shows value
              secondaryTitle={
                isSpecificMode
                  ? (isQuantity
                      ? FormatToShortPriceTemplate(totalMonetary)
                      : `${formatToShortString(totalQty, 0)} ${qtyUnitLabel}`)
                  : '' // General mode: no secondary (qty across mixed units is meaningless)
              }
              ratioLine={ratioLine}
              cssSize="w-52 h-52"
              isQuantity={isQuantity}
              quantityUnitLabel={isQuantity ? qtyUnitLabel : undefined}
            />
          </div>
        )}
        <div className={compact ? "h-full relative" : "h-[12.5rem] relative"}>
          {data && data.length > 0 ? (
            <StackedBarChart
              stackedBarChartData={data}
              terminals={isTerminals ? terminals : []}
              unit={''}
              isGeneral={formulaId === GENERAL_ID}
              setMaxVal={setMaxVal}
              chartMax={chartMax}
              name={cardTitle}
              showProportion={showProportion}
            />
          ) : (
            <div className="flex items-center justify-center h-full text-gray-400">
              <div className="text-center">
                <p className="text-sm">No data available</p>
                <p className="text-xs mt-1">Data length: {data?.length || 0}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

type StackedBarChartProps = {
  stackedBarChartData: StackedBarChartElement[];
  xAxisTitle?: string;
  terminals: number[];
  firstMonthName?: string;
  isCumulative?: boolean;
  isGeneral?: boolean;
  unit?: string;
  setMaxVal: (_max: number) => void;
  chartMax: number;
  name: string;
  showProportion: boolean;
};

export const StackedBarChart: React.FC<StackedBarChartProps> = ({
  stackedBarChartData,
  xAxisTitle,
  terminals,
  firstMonthName,
  isCumulative = false,
  isGeneral = false,
  unit = '$',
  setMaxVal,
  chartMax,
  name: _name,
  showProportion,
}) => {
  const { year, month: origMonth } = convertDateToMonthYear(new Date());
  // Use helper to get previous month safely (handles year boundaries)
  const prevMonthYear = changeMonthYearBy({ year, month: origMonth }, -1);
  const origFirstMon = getMonthName(prevMonthYear);

  const labels = [
    ...Array.from(
      new Set(
        stackedBarChartData
          .reduce((acc, itm) => [...acc, ...itm.valueArray.map(({ date }) => date)], [] as MonthYear[])
          .sort(sortMonthYear)
          .filter((a, i) => terminals.length === 0 || terminals.includes(a.month - 1) || (firstMonthName && i === 0))
          .map(getMonthName),
      ),
    ),
    ...(firstMonthName ? [origFirstMon] : []),
  ];
  React.useEffect(() => {
    if (stackedBarChartData && showProportion) {
      const max = calcMaxVal(stackedBarChartData, isGeneral);
      setMaxVal(max);
    }
  }, [stackedBarChartData, isGeneral, showProportion]);

  const [firstMonth, ...otherMonths] = labels;

  const finalLabels = firstMonthName
    ? [firstMonthName, ...otherMonths, ...(terminals.includes(origMonth - 2) ? [origFirstMon] : [])]
    : labels.length > 0
    ? labels
    : months;

  const getTerminalValue = (valueArray: MonthYearValue[], termName: string): { value: number } => {
    const terminalIndex = months.indexOf(termName);
    const monthsOfTerm: number[] = [terminalIndex];
    for (let i = 1; i < 4; i++) {
      const prevMonth = (12 + terminalIndex - i) % 12;
      if (terminals.includes(prevMonth)) {
        break;
      }
      monthsOfTerm.push(prevMonth);
    }

    const value =
      valueArray.filter(({ date }) => monthsOfTerm.includes(date.month - 1))?.reduce((a, { value }) => a + value, 0) ||
      0;
    return { value };
  };

  const now = new Date();
  const datasets = stackedBarChartData
    .filter((a) => !!a.fieldName)
    .map((item) => ({
      label: item.fieldName,
      data: labels.map((shortLabel, monthInd) => {
        const dataPoint =
          terminals.length > 0 && !isCumulative
            ? getTerminalValue(item.valueArray, shortLabel)
            : item.valueArray.find(
                (value) =>
                  getMonthName(value.date) === shortLabel && (monthInd < 12 || value.date.year > now.getFullYear()),
              );

        return dataPoint ? Number(dataPoint.value).toFixed(0) : 0;
      }),
      backgroundColor: (ctx: any) => {
        // Special color for on-hand inventory (first bar if it exists)
        if (labels[ctx.index] === 'Stg') {
          return '#10B981'; // Green color for on-hand inventory
        }
        // Special color for first month in specific mode
        if (!isGeneral && firstMonthName && ctx.index === 0) {
          return selectBlue;
        }
        return item.color;
      },
      // Only apply rounded corners in specific mode (not general mode with stacked bars)
      borderRadius: isGeneral ? 0 : 4,
      stack: 'combined',
    }));

  const data = { labels: finalLabels, datasets };

  const options = {
    responsive: true,
    barPercentage: 1,
    categoryPercentage: 1,
    maxBarThickness: 15,
    minBarThickness: 10,
    maintainAspectRatio: false,

    plugins: {
      legend: {
        display: false,
      },
    },

    scales: {
      x: {
        title: {
          display: false,
        },
        ticks: {
          color: (context: any) => (firstMonthName && context.tick.value === 0 ? selectBlue : 'rgba(0, 0, 0, 0.5)'),
        },

        grid: {
          drawOnChartArea: false,
          drawTicks: false,
        },
      },

      y: {
        ...(showProportion && chartMax > 0 ? { min: 0, max: chartMax } : {}),
        title: {
          display: false,
          text: 'USD in millions',
        },
        stacked: true,
        ticks: {
          count: 5,
          callback: function (value: any) {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { dataSet } = { dataSet: [] };
            return FormatToShortPriceTemplate(value, unit, 1).trim();
          },
          autoSkip: true,
          maxTicksLimit: 6,
        },
        grid: {
          color: (context: any) => {
            const value = context.tick.value as number;
            if (value === 0) {
              return '#AFB0B0'; // Change grid color to red for values greater than 5
            }
            return 'rgba(0, 0, 0, 0.1)'; // Default grid color
          },
          drawOnChartArea: true,
          drawTicks: true, // Disable grid lines on the tick marks
        },
        // Fix width to prevent chart from jumping when switching modes
        afterFit: (scaleInstance: any) => {
          scaleInstance.width = 60; // Fixed width for Y-axis labels
        },
      },
    },
  };

  return <Bar className="" data={data} options={options} />;
};
